import { createStore, createEffect, createEvent, combine } from 'effector';
import i18n from 'i18n';
import { $notificationStore } from '../notificationStore';
import { ApiUtils } from '@shared/utils';
import { ProfileApi, UsersApi } from '@shared/api/rest';
import type { UserUpdateWithIdRequest } from '@shared/api/rest/users';
import { RequestStrategyEnum, type IQueryParams } from '@shared/api/models';
import { ProfileResponse } from '@shared/api/rest/profile/models';

const getProfileEffect = createEffect((params?: IQueryParams) => ProfileApi.getProfile(params?.signal));
const updateProfileEffect = createEffect((request: UserUpdateWithIdRequest) => UsersApi.updateUser(request));
const clearEvent = createEvent();

const $dataStore = createStore<ProfileResponse | null>(null)
  .on(getProfileEffect.done, (_, data) => data.result ?? null)
  .on(updateProfileEffect.done, (current, data) => (data.result as unknown as ProfileResponse) ?? current)
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(getProfileEffect.pending, (_, data) => data)
  .on(getProfileEffect.fail, () => false)
  .on(getProfileEffect.done, () => false)
  .on(updateProfileEffect.pending, (_, data) => data)
  .on(updateProfileEffect.fail, () => false)
  .on(updateProfileEffect.done, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getProfileEffect, () => null)
  .on(getProfileEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getProfileEffect.done, () => null)
  .on(updateProfileEffect, () => null)
  .on(updateProfileEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(updateProfileEffect.done, () => null)
  .reset(clearEvent);
$errorStore.watch((x) => $notificationStore.events.setError(x));

export const $profileStore = {
  events: {
    getProfile: ApiUtils.wrapperApi(getProfileEffect, RequestStrategyEnum.Debounce),
    updateProfile: updateProfileEffect,
    clear: clearEvent,
  },
  store: combine({
    loading: $loadingStore,
    data: $dataStore,
    error: $errorStore,
  }),
};
